import { LegalLayout } from "@/components/site/LegalLayout";

const LAST_UPDATED = "July 14, 2026";

const intro = [
  {
    type: "p",
    text: "The World Financial Innovation Institute (\"WFII\", \"we\", \"us\", or \"our\") is a Canada-based educational institute providing professional education, certification, research, and resources on digital finance. We are committed to protecting the privacy of the individuals and organizations that interact with us. This Privacy Policy explains what personal information we collect, how we use and share it, and the choices available to you.",
  },
  {
    type: "p",
    text: "This Policy applies to our website, our educational programs and certifications, our research and resource publications, our membership and partnership offerings, and any other services that link to this Policy (collectively, the \"Services\").",
  },
];

const sections = [
  {
    id: "who-we-are",
    title: "Who We Are",
    body: [
      {
        type: "p",
        text: "WFII is an educational institute based in Canada. We help CFOs, accountants, bookkeepers, treasury teams, compliance professionals, and business leaders understand digital money, stablecoins, digital assets, payments, accounting, treasury, and compliance. For the purposes of applicable privacy laws, including the Personal Information Protection and Electronic Documents Act (PIPEDA) and substantially similar provincial legislation, WFII is the organization responsible for the personal information collected through the Services.",
      },
    ],
  },
  {
    id: "information-we-collect",
    title: "Information We Collect",
    body: [
      {
        type: "p",
        text: "We collect personal information in three main ways: information you provide to us directly, information collected automatically when you use the Services, and information we receive from third parties.",
      },
      {
        type: "ul",
        items: [
          "Contact and identity information — such as your name, email address, telephone number, employer, job title, and professional designation, provided when you contact us, register for a program, apply for membership, subscribe to updates, or download a resource.",
          "Program and certification information — such as enrollment records, course progress, assessment results, certification status, and continuing education records.",
          "Membership and partnership information — such as organization details, billing contacts, and information needed to administer individual, corporate, or knowledge partner relationships.",
          "Payment information — such as billing name, billing address, and transaction details. Payment card details are processed by third-party payment processors and are not stored on our systems.",
          "Communications — such as emails, form submissions, event registrations, survey responses, and feedback you share with us.",
          "Technical and usage information — such as IP address, browser type, device identifiers, operating system, referring pages, pages viewed, and the dates and times of visits, collected through cookies and similar technologies as described in our Cookie Policy.",
        ],
      },
    ],
  },
  {
    id: "how-we-use-information",
    title: "How We Use Your Information",
    body: [
      { type: "p", text: "We use personal information for the following purposes:" },
      {
        type: "ul",
        items: [
          "To provide and administer the Services, including programs, certifications, memberships, partnerships, events, and resources.",
          "To process registrations, enrollments, applications, and payments.",
          "To maintain accurate education, certification, and continuing education records.",
          "To respond to inquiries and provide support.",
          "To send administrative communications, such as confirmations, updates to schedules, and changes to policies.",
          "To send educational updates, research publications, newsletters, and event invitations where permitted by law. You may unsubscribe at any time.",
          "To improve our website, programs, and resources, including through aggregated and de-identified analytics.",
          "To protect the security and integrity of the Services and to prevent fraud or misuse.",
          "To comply with legal and regulatory obligations.",
        ],
      },
    ],
  },
  {
    id: "consent",
    title: "Consent",
    body: [
      {
        type: "p",
        text: "We collect, use, and disclose personal information with your consent, or as otherwise permitted or required by applicable law. Consent may be express (for example, when you submit a form or opt in to communications) or implied (for example, when you provide information that is reasonably necessary to deliver a service you have requested).",
      },
      {
        type: "p",
        text: "You may withdraw your consent at any time, subject to legal or contractual restrictions and reasonable notice. Withdrawing consent may affect our ability to provide certain Services, such as maintaining an active membership or certification record.",
      },
    ],
  },
  {
    id: "cookies",
    title: "Cookies and Similar Technologies",
    body: [
      {
        type: "p",
        text: "We use cookies and similar technologies to operate the website, remember preferences, and understand how the Services are used. For a detailed explanation of the technologies we use and the choices available to you, please review our Cookie Policy, available in the footer of this website.",
      },
    ],
  },
  {
    id: "how-we-share-information",
    title: "How We Share Information",
    body: [
      { type: "p", text: "We do not sell personal information. We may share personal information in the following circumstances:" },
      {
        type: "ul",
        items: [
          "Service providers — with vendors who perform services on our behalf, such as website hosting, payment processing, email delivery, event management, and analytics. These providers are contractually required to protect personal information and to use it only for the purposes we specify.",
          "Corporate members and sponsors of training — where your participation in a program is arranged or funded by your employer or another organization, we may share enrollment status, completion, and certification outcomes with that organization.",
          "Certification verification — where you hold a WFII credential, we may confirm the status of that credential to third parties who request verification, to the extent reasonably necessary to maintain the integrity of the certification.",
          "Legal requirements — where disclosure is required or permitted by law, such as in response to a court order, subpoena, or lawful request by a public authority.",
          "Business transactions — in connection with a merger, reorganization, or transfer of all or part of our operations, subject to appropriate confidentiality protections.",
        ],
      },
    ],
  },
  {
    id: "international-transfers",
    title: "International Data Transfers",
    body: [
      {
        type: "p",
        text: "WFII is based in Canada, and personal information is primarily stored and processed in Canada. Some of our service providers may store or process information in other jurisdictions, including the United States. When personal information is processed outside of Canada, it may be subject to the laws of those jurisdictions, and courts, law enforcement, and national security authorities in those jurisdictions may be able to access it in accordance with their laws. We take reasonable steps, including contractual protections, to ensure that personal information transferred to service providers receives a comparable level of protection.",
      },
    ],
  },
  {
    id: "data-retention",
    title: "Data Retention",
    body: [
      {
        type: "p",
        text: "We retain personal information only as long as necessary to fulfil the purposes for which it was collected, to satisfy legal, accounting, or reporting requirements, and to maintain records that support the ongoing validity of certifications and continuing education. Retention periods vary by record type. For example, certification records may be retained for as long as the credential remains verifiable, while marketing preferences are retained until you unsubscribe. When personal information is no longer required, we securely delete or de-identify it.",
      },
    ],
  },
  {
    id: "data-security",
    title: "Data Security",
    body: [
      {
        type: "p",
        text: "We maintain administrative, technical, and physical safeguards designed to protect personal information against loss, theft, and unauthorized access, use, disclosure, or modification. These safeguards include access controls, encryption in transit, staff confidentiality obligations, and vendor due diligence. No method of transmission or storage is completely secure, and we cannot guarantee absolute security; however, we review and update our practices as technologies and risks evolve.",
      },
    ],
  },
  {
    id: "your-rights",
    title: "Your Rights and Choices",
    body: [
      { type: "p", text: "Subject to applicable law, you have the following rights with respect to your personal information:" },
      {
        type: "ul",
        items: [
          "Access — you may request access to the personal information we hold about you and information about how it has been used and disclosed.",
          "Correction — you may request that we correct inaccurate or incomplete personal information.",
          "Withdrawal of consent — you may withdraw consent to the collection, use, or disclosure of your personal information, subject to legal or contractual restrictions.",
          "Marketing preferences — you may unsubscribe from marketing communications at any time by using the unsubscribe link in our emails or by contacting us.",
          "Complaints — you may raise a concern with us directly, and you also have the right to file a complaint with the Office of the Privacy Commissioner of Canada or your provincial privacy regulator.",
        ],
      },
      {
        type: "p",
        text: "To exercise any of these rights, please contact us using the details in the Contact section below. We may need to verify your identity before acting on your request, and we will respond within the timelines required by applicable law.",
      },
    ],
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    body: [
      {
        type: "p",
        text: "The Services are intended for professionals and organizations and are not directed to children. We do not knowingly collect personal information from individuals under the age of majority in their province or territory of residence. If you believe that a child has provided personal information to us, please contact us and we will take appropriate steps to delete it.",
      },
    ],
  },
  {
    id: "third-party-links",
    title: "Third-Party Links",
    body: [
      {
        type: "p",
        text: "The Services may contain links to third-party websites, tools, and resources that are not operated by WFII. This Privacy Policy does not apply to those third parties, and we are not responsible for their privacy practices. We encourage you to review the privacy policies of any third-party services you use.",
      },
    ],
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    body: [
      {
        type: "p",
        text: "We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, or legal requirements. When we make material changes, we will update the \"Last updated\" date at the top of this page and, where appropriate, provide additional notice through the Services. We encourage you to review this Policy periodically.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    body: [
      {
        type: "p",
        text: "If you have questions about this Privacy Policy, wish to exercise your rights, or would like to raise a privacy concern, please contact our Privacy Office at privacy@wfii.ca. We will acknowledge your request and respond within a reasonable time and in accordance with applicable law.",
      },
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated={LAST_UPDATED}
      intro={intro}
      sections={sections}
      testId="privacy-policy-page"
    />
  );
}
